import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, InputBar, Textarea } from '~/ui';
import CustomQuerySelect from '../Shared/CustomQuerySelect/CustomQuerySelect';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import PromotionConditionBoard from './PromotionConditionBoard/PromotionConditionBoard';
import { useContext, useEffect, useRef, useState } from 'react';
import { ICondition, IStock } from '../PromotionsBoard/StockBoard/StockBoard';
import { IConditionGroup } from './PromotionConditionBoard/ConditionGroupCard/ConditionGroupCard';
import PhotoIcon from '~/assets/svg/knowledge/photo.svg?react';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import { Modal } from '../Shared/Modal/Modal';
import { useQueryClient } from '@tanstack/react-query';
import { ICreatePromotionProps } from '~/pages/CreatePromotion/CreatePromotion';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import AssetImageContainer from '../CreateArticle/AssetImageContainer/AssetImageContainer';
import { validate } from '~/utils/validateStock';
import { v4 as uuidv4 } from 'uuid';

import cn from 'classnames';
import styles from './PromotionForm.module.scss';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import { AuthenticationContext } from '../Authentication/AuthenticationProvider';

interface IFormikValues {
  name: string;
  description: string;
  status: number | null | undefined;
  asset: { id: number; name: string };
  accommodations: { id: number; name: string }[];
  organizations: { id: number; name: string }[];
  date: { startDate: Dayjs | null; endDate: Dayjs | null };
  points: string;
  days_action: string;
  image: { id: number; full_photo_url: string; path: string };
  conditionGroups: IConditionGroup[];
  accommodation_all: boolean;
  organization_all: boolean;
}

interface IPromotionFormProps {
  isEdit?: boolean;
  data?: IStock | undefined;
  onSubmit: (values: ICreatePromotionProps) => Promise<void>;
}

const STATUS = {
  active: 1,
  waiting: 2,
  archive: 3,
};

const createEmptyGroup = (): IConditionGroup => ({
  id: uuidv4(),
  operator: 'and',
  bindings: [],
});

const FIELD_ORDER = [
  'name',
  'description',
  'asset',
  'accommodations',
  'organizations',
  'date',
  'points',
  'days_action',
  'image',
  'conditionGroups',
];

const PromotionForm = ({
  isEdit,
  data,
  onSubmit,
}: IPromotionFormProps) => {
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toastError = useToastError();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);
  const isLoadingInitialData = useRef(false);

  const {
    values,
    handleBlur,
    handleChange,
    setValues,
    errors,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    submitCount,
  } = useFormik<IFormikValues>({
    initialValues: {
      name: '',
      description: '',
      status: 3,
      asset: { id: 0, name: '' },
      accommodations: [],
      organizations: [],
      date: { startDate: null, endDate: null },
      points: '',
      days_action: '',
      image: { id: 0, full_photo_url: '', path: '' },
      conditionGroups: [createEmptyGroup()],
      accommodation_all: false,
      organization_all: false,
    },
    validate,
    onSubmit: values => {
      const start_date = dayjs(values.date.startDate).format('YYYY-MM-DD');
      const end_date = dayjs(values.date.endDate).format('YYYY-MM-DD');

      const accommodations = values.accommodations.map(item => item.id);
      const organizations = values.organizations.map(item => item.id);

      const rules = values.conditionGroups.map((group, index) => ({
        operator: group.operator,
        ...(index > 0 ? { inter_operator: group.inter_operator || 'and' } : {}),
        bindings: group.bindings.map(item => ({
          rule_id: item.description.id,
          value: Number(item.meaning),
        })),
      }));

      onSubmit({
        name: values.name,
        description: values.description,
        start_date,
        end_date,
        asset_id: values.asset.id,
        status_id: 1,
        burn_count_days: Number(values.days_action) || 0,
        calculated_points: Number(values.points) || 0,
        piwigo_image_id: values.image.id,
        ...(values.accommodation_all
          ? {}
          : { accommodation_ids: accommodations }),
        ...(values.organization_all ? {} : { organization_ids: organizations }),
        ...(values.accommodation_all
          ? { accommodation_all: values.accommodation_all }
          : {}),
        ...(values.organization_all
          ? { organization_all: values.organization_all }
          : {}),
        rules,
      });
    },
  });

  const submitted = submitCount > 0;

  const hasError = (field: string) => {
    if (!submitted) return false;
    if (field === 'date') return !!(errors as any)?.date;
    return !!(errors as any)?.[field];
  };

  const scrollToFirstError = () => {
    if (!formRef.current) return;
    for (const field of FIELD_ORDER) {
      if ((errors as any)?.[field]) {
        const el = formRef.current.querySelector(`[data-field="${field}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          break;
        }
      }
    }
  };

  useEffect(() => {
    if (submitted && Object.keys(errors).length > 0) {
      scrollToFirstError();
    }
  }, [submitCount, errors]);

  useEffect(() => {
    if (isLoadingInitialData.current) {
      isLoadingInitialData.current = false;
      return;
    }
    setFieldValue('organizations', []);
  }, [values.accommodations, values.accommodation_all]);

  const loadAssetById = async (id: number | undefined) => {
    try {
      const { data } = await instance.get(`admin/assets/${id}`);
      queryClient.setQueryData(['asset', id], data);
      setFieldValue('asset', data);
    } catch (error) {
      toastError(error);
    }
  };

  const handleAddCondition = (groupId: string, condition: ICondition) => {
    const updatedGroups = values.conditionGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, bindings: [...group.bindings, condition] };
      }
      return group;
    });
    setFieldValue('conditionGroups', updatedGroups);
  };

  const handleDeleteCondition = (
    groupId: string,
    conditionId: string,
    _item: { rule_id: number }
  ) => {
    const updatedGroups = values.conditionGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          bindings: group.bindings.filter(b => b.id !== conditionId),
        };
      }
      return group;
    });
    setFieldValue('conditionGroups', updatedGroups);
  };

  const handleEditCondition = (groupId: string, condition: ICondition) => {
    const updatedGroups = values.conditionGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          bindings: group.bindings.map(b =>
            b.id === condition.id ? condition : b
          ),
        };
      }
      return group;
    });
    setFieldValue('conditionGroups', updatedGroups);
  };

  const handleOperatorChange = (groupId: string, operator: 'and' | 'or') => {
    const updatedGroups = values.conditionGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, operator };
      }
      return group;
    });
    setFieldValue('conditionGroups', updatedGroups);
  };

  const handleAddGroup = (interOperator: 'and' | 'or') => {
    setFieldValue('conditionGroups', [
      ...values.conditionGroups,
      { ...createEmptyGroup(), inter_operator: interOperator },
    ]);
  };

  const handleDeleteGroup = (groupId: string) => {
    setFieldValue(
      'conditionGroups',
      values.conditionGroups.filter(g => g.id !== groupId)
    );
  };

  useEffect(() => {
    if (isEdit && data) {
      isLoadingInitialData.current = true;
      loadAssetById(data?.asset_id);

      const accommodations = data?.accommodations
        .map(item => {
          if (item.accommodations) {
            return {
              id: item.accommodations.id,
              name: item.accommodations.full_name,
            };
          }
          return null;
        })
        .filter(Boolean) as { id: number; name: string }[];

      let conditionGroups: IConditionGroup[];

      if (data.conditionGroups && data.conditionGroups.length > 0) {
        conditionGroups = data.conditionGroups.map((group, index) => ({
          id: uuidv4(),
          operator: (group.operator as 'and' | 'or') || 'and',
          ...(index > 0 ? { inter_operator: (group.inter_operator as 'and' | 'or') || 'and' } : {}),
          bindings: (group.bindings || []).map((item: any) => ({
            id: String(item.id),
            department: {
              id: item.rule?.module_id || 0,
              name: item.rule?.module?.name || '',
            },
            description: {
              id: item.rule_id,
              name: item.rule?.description || '',
            },
            meaning: String(item.value),
          })),
        }));
      } else {
        const bindings = data.rules.map(item => ({
          id: String(item.id),
          department: {
            id: item.rule.module_id || 0,
            name: item.rule.module.name || '',
          },
          description: { id: item.rule_id, name: item.rule.description },
          meaning: String(item.value),
        }));
        conditionGroups = [
          {
            id: uuidv4(),
            operator: 'and',
            bindings,
          },
        ];
      }

      setValues({
        name: data.name,
        description: data.description,
        status: data.status_id,
        asset: { id: 0, name: '' },
        accommodations,
        organizations: data?.organizations,
        date: {
          startDate: dayjs(data.start_date),
          endDate: dayjs(data.end_date),
        },
        points: String(data.calculated_points),
        days_action: String(data.burn_count_days),
        image: !!data.photos.length
          ? data.photos[0]
          : { id: 0, path: '', full_photo_url: '' },
        conditionGroups,
        accommodation_all: false,
        organization_all: false,
      });
    } else {
      return;
    }
  }, [data]);

  return (
    <div className={styles.formWrapper}>
      <form onSubmit={handleSubmit} className={styles.form} ref={formRef} noValidate>
        <div data-field="name">
          <InputBar
            label={t('name_of_action')}
            name="name"
            value={values.name}
            placeholder={t('name_of_action')}
            star
            disabled={isEdit && values.status !== STATUS.waiting}
            onChange={handleChange}
            onBlur={handleBlur}
            error={hasError('name')}
          />
        </div>

        <div data-field="description">
          <Textarea
            label={t('description')}
            rows={3}
            star
            maxLength={1000}
            name="description"
            placeholder={t('description')}
            disabled={isEdit && values.status !== STATUS.waiting}
            value={values.description}
            onChange={handleChange}
            onBlur={() => setFieldTouched('description', true)}
            error={hasError('description')}
          />
        </div>

        <div className={styles.assetWrapper}>
          <div className={styles.querySelectWrapper} data-field="asset">
            <CustomQuerySelect
              label={t('assets')}
              query="admin/assets"
              queryKey={['assets', {}]}
              placeholder={t('validate_asset')}
              params={null}
              value={values.asset}
              className={styles.querySelect}
              onChange={value => {
                setFieldValue('asset', value);
                setFieldValue('accommodations', []);
                setFieldValue('accommodation_all', false);
              }}
              required
              disabled={isEdit && values.status !== STATUS.waiting}
              onTouch={() => setFieldTouched('asset', true)}
              errorBorder={hasError('asset')}
            />
          </div>

          <div className={styles.querySelectWrapper} data-field="accommodations">
            <CustomQuerySelect
              label={t('accommodation_facility')}
              className={styles.querySelect}
              onChange={value => {
                setFieldValue('accommodations', value);
              }}
              value={values.accommodations}
              query="admin/accommodations"
              queryKey={[
                'accommodations-100',
                {
                  sort: '',
                  perPage: 100,
                  field: '',
                  page: 1,
                  asset_id: values.asset.id,
                },
              ]}
              params={{
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
                asset_id: values.asset.id,
              }}
              required
              placeholder={t('to_choose')}
              multiselect
              isSearchEnabled
              onTouch={() => setFieldTouched('accommodations', true)}
              errorBorder={hasError('accommodations')}
              disabled={
                (values.asset.id === 0 && !values.asset.name) ||
                (isEdit && values.status !== STATUS.waiting) ||
                values.accommodation_all
              }
              hasCheckbox={
                (isEdit && values.status === STATUS.waiting && !isSuperAdmin) ||
                (!isEdit &&
                  values.asset.id !== 0 &&
                  !!values.asset.name &&
                  !isSuperAdmin)
              }
              checkboxLabel={t('all_accommodations')}
              setIsCheckedAll={value =>
                setFieldValue('accommodation_all', value)
              }
            />
          </div>

          <div className={styles.querySelectWrapper} data-field="organizations">
            <CustomQuerySelect
              label={t('organizations')}
              query="admin/organization"
              onChange={value => {
                setFieldValue('organizations', value);
              }}
              className={styles.querySelect}
              placeholder={t('validate_organizations')}
              value={values.organizations}
              isSearchEnabled
              queryKey={[
                'organizations',
                {
                  sort: '',
                  field: '',
                  page: 1,
                  asset_id: values.asset.id,
                  accommodation_ids: values.accommodations.map(i => i.id),
                },
              ]}
              params={{
                sort: '',
                field: '',
                page: 1,
                asset_id: values.asset.id,
                accommodation_ids: values.accommodations.map(i => i.id),
              }}
              onTouch={() => setFieldTouched('organizations', true)}
              errorBorder={hasError('organizations')}
              required
              multiselect
              disabled={
                (isEdit && values.status !== STATUS.waiting) ||
                values.organization_all
              }
              hasCheckbox={
                (isEdit && values.status === STATUS.waiting && !isSuperAdmin) ||
                (!isEdit && !isSuperAdmin)
              }
              checkboxLabel={t('all_organizations')}
              setIsCheckedAll={value =>
                setFieldValue('organization_all', value)
              }
            />
          </div>
        </div>

        <div className={styles.valuesWrapper}>
          <label className={styles.dateWrapper} data-field="date">
            <span className={styles.dateLabel}>{t('stock_date')}<span style={{ color: 'red' }}>*</span></span>
            <DatePicker.RangePicker
              className={cn(styles.datepicker, {
                [styles.datepickerError]: hasError('date'),
              })}
              format="DD.MM.YYYY"
              value={[values.date.startDate, values.date.endDate]}
              onChange={date => {
                if (date && date[0] && date[1]) {
                  setFieldValue('date.startDate', date[0]);
                  setFieldValue('date.endDate', date[1]);
                } else {
                  setFieldValue('date.startDate', dayjs());
                  setFieldValue('date.endDate', dayjs());
                }
              }}
              onBlur={() => {
                setFieldTouched('date.startDate', true);
                setFieldTouched('removaldate.endDate_date', true);
              }}
              placeholder={[t('date'), t('date')]}
              disabledDate={current =>
                current && current.isBefore(dayjs(), 'day')
              }
              disabled={isEdit}
            />
          </label>
          <div data-field="points" className={styles.valuesField}>
            <InputBar
              label={t('points_awarded_quantity')}
              name="points"
              value={String(values.points)}
              placeholder={t('points_awarded_quantity')}
              star
              disabled={isEdit && values.status !== STATUS.waiting}
              onChange={handleChange}
              onBlur={handleBlur}
              error={hasError('points')}
            />
          </div>
          <div data-field="days_action" className={styles.valuesField}>
            <InputBar
              label={t('points_days_valid')}
              name="days_action"
              value={String(values.days_action)}
              placeholder={t('points_days_valid')}
              star
              disabled={isEdit && values.status !== STATUS.waiting}
              onChange={handleChange}
              onBlur={handleBlur}
              error={hasError('days_action')}
            />
          </div>
        </div>

        <div data-field="image">
          {values.image.id !== 0 ? (
            <div className={styles.imagesWrapper}>
              <p className={styles.imageTitle}>{t('cover')}</p>
              <div className={styles.imageItem}>
                <img src={values.image.full_photo_url} className={styles.image} alt="" />
                {!isEdit && values.status !== STATUS.waiting && (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => {
                      if (isEdit && values.status !== STATUS.waiting) return;
                      setFieldValue('image', {
                        id: 0,
                        full_photo_url: '',
                        path: '',
                      });
                    }}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.addIconWrapper}>
              <IconButton
                onClick={() => {
                  if (values.asset.id === 0) return;
                  setIsModalOpen(true);
                }}
                className={cn(styles.pickPhotoButton, {
                  [styles.disabledPhotoButton]: values.asset.id === 0,
                  [styles.pickPhotoButtonError]: hasError('image'),
                })}
                isDisabled={isEdit && values.status !== STATUS.waiting}
              >
                <PhotoIcon className={styles.icon} />
                <span className={styles.pickPhotoText}>
                  {t('select_stock_cover')}
                </span>
              </IconButton>
            </div>
          )}
        </div>

        <div className={styles.conditionWrapper} data-field="conditionGroups">
          <PromotionConditionBoard
            conditionGroups={values.conditionGroups}
            onOperatorChange={handleOperatorChange}
            onAddCondition={handleAddCondition}
            onDeleteCondition={handleDeleteCondition}
            onEditCondition={handleEditCondition}
            onDeleteGroup={handleDeleteGroup}
            onAddGroup={handleAddGroup}
            isEdit={isEdit}
            disabled={isEdit && values.status !== STATUS.waiting}
            hasError={hasError('conditionGroups')}
          />
        </div>

        <Button
          type="submit"
          text={t('save_n_finish')}
          className={styles.submitBtn}
        />
      </form>

      {values.asset.id !== 0 && (
        <Modal
          modalStyles={styles.photosModal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <AssetImageContainer
            path="promo-loyalty-images"
            queryKey="promotion-images"
            assetId={values.asset.id}
            selectedImage={values.image}
            onCloseModal={() => setIsModalOpen(false)}
            onSelectImages={values => setFieldValue('image', values)}
          />
        </Modal>
      )}
    </div>
  );
};

export default PromotionForm;
